

import { type GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep} from 'lodash';
import React, { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomStatus,
  CustomTooltipWithLabel,
  DashboardLoader,
  renderHeaderActionButtons,
  SelectWithActionButton,
  SidePopover,
  TableDashboard,
  useAppDispatch,
  useAppSelector,
  type ISelectWithActionButtonOption,
} from '../../../globalUtils/globalExports';


import {
  getAcCertificatesReducer,
  fetchAcChargerCertificatesInfoData,
  setAcChargerCertificatesInfoListTableProps,
  acCertificatesTypes,
  getCurrentScreenAppliedAcFiltersCount,
  setAcFilterData,
  getAcFilterSelectedValue,
  getAcFilterReducer,
  getAcReferenceDataReducer,
  getCurrentAcFiltersValues,
  pushAcChargerCertificate,
} from '../../rmsReduxStore/acReduxExports';
import { NoDataComponentUsbCertificates } from '../../../globalUtils/TableDashboard/TableComponents';
import { downloadFile, formatRelativeTimeForTable } from '../../../globalUtils/globalHooks';
import access from '../../../auth/service/AccessControl';


import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getGlobalReducer, getReferenceDataReducer, setTableColumnPrefrences } from '../../../rmsReduxStore/reduxExports';
import AcFilterLayout,{ FilterProp } from '../../../globalUtils/FilterLayout/AcFilterLayout';





const deployOptionsList: ISelectWithActionButtonOption[] = [
  { id: 'key', value: 'key', label: 'Client Private Key' },
  { id: 'cert', value: 'cert', label: 'Client Certificate' },
  { id: 'rootca', value: 'rootca', label: 'Root CA Certificate' },
]

const deployButtonTooltip = (
  <ul className='select__with__action__button-tooltip-list'>
    <li>Deploy the &apos;Client Private Key&apos; and &apos;Client Certificate&apos; in any order, one at a time.</li>
    <li>Wait 1 minute after each deployment confirmation before uploading the next.</li>
    <li>Deploy the &apos;Root CA Certificate&apos; last.</li>
  </ul>
)

const AcChargerCertificatesInfo = memo(function AcChargerCertificatesInfo() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const layouts = ['list', 'map'];
  const { acChargerCertificatesInfo, loader, acChargerCertificatesInfoListTableProps,acChargerCertificatesInfoCount } = useAppSelector(getAcCertificatesReducer);
  const acChargerCertificatesInfoListTablePropsRef = useRef(acChargerCertificatesInfoListTableProps);
  const acChargerCertificatesInfoRef = useRef(acChargerCertificatesInfo);
  const {screenTableColumnPrefrences} = useAppSelector(getGlobalReducer)
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [selectedDeployOptions, setSelectedDeployOptions] = useState<Record<string, ISelectWithActionButtonOption>>({});
  const [isDeployingCertificate, setIsDeployingCertificate] = useState(false);
  const { screenFilters } = useAppSelector(getAcFilterReducer)
  const {
    chargingStationReferenceData,
    chargerProductTypeReferenceData,
    chargerVisibleIdReferenceData,
  } = useAppSelector(getAcReferenceDataReducer);
  const { certificateStatusReferenceData,userReferenceData } = useAppSelector(getReferenceDataReducer);

  

  // Warn before reload / tab close while a certificate is being deployed
  useEffect(() => {
    if (!isDeployingCertificate) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDeployingCertificate]);

  useEffect(() => {
    acChargerCertificatesInfoListTablePropsRef.current = acChargerCertificatesInfoListTableProps;
  }, [acChargerCertificatesInfoListTableProps]);

  useEffect(() => {
    acChargerCertificatesInfoRef.current = acChargerCertificatesInfo;
  }, [acChargerCertificatesInfo]);

  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (acChargerCertificatesInfoListTablePropsRef?.current) {
      await layoutCallback(1, acChargerCertificatesInfoListTablePropsRef?.current?.pageSize, 'list', acChargerCertificatesInfoListTablePropsRef?.current?.sortBy, acChargerCertificatesInfoListTablePropsRef?.current?.order, null, searchTerm)
    }
  }, [acChargerCertificatesInfo, acChargerCertificatesInfoListTablePropsRef?.current, acChargerCertificatesInfoRef?.current]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-charger-parameters-search-box',
      searchFieldName: 'manage-charger-parameters-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText]);



  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'add_new_certificate',
          isAccessible: access?.chargers?.add() || access?.chargers?.edit(),
          headerActionBtnClick: (): void => {
            navigate('/ac/add-charger-certificate');
          }
        },
        {
          id: 'add_filter_btn',
          isAccessible: access?.chargers?.view(),
          headerActionBtnClick: (): void => {
            setFiltersVisible(true);
          },
          badgeCount: getCurrentScreenAppliedAcFiltersCount('acChargerCertificates')
        },
        // {

        //   id: 'export_csv_btn',
        //   isAccessible: access?.chargers?.export(),
        //   headerActionBtnClick: (event): void => {
        //     setExportAnchorEl(event.currentTarget);

        //   }
        // },
      ]
  })

  const handleFiltersClosePopOver = (): void => {
    setFiltersVisible(false);
  }


  const getTableRows = useCallback((): IAcChargerCertificatesInfo[] => {
    return acChargerCertificatesInfo !== null && acChargerCertificatesInfo?.length > 0 ? acChargerCertificatesInfo : [];
  }, [acChargerCertificatesInfo]);


  
  const onDownloadFileIconClick = (fileURL) => {
    if(!fileURL) {
      return toast.warn('No certificates present.');
    }
    try{
      downloadFile({ url: fileURL });
      return toast.success('File downloaded.');
    } catch{
      return toast.error('Download failed.');
    }

}

  const onDeployOptionClick = async (rowData, fileKey) => {
    if (!rowData?.id || isDeployingCertificate) return;
    setIsDeployingCertificate(true);
    try {
      const { data, status } = await dispatch(pushAcChargerCertificate({ certId: rowData.id, fileType: fileKey }));
      if (status === 200) {
        toast.success(data?.message ?? 'Certificate successfully pushed to the charger.');
      } else {
        toast.error(data?.message ?? 'Failed to push certificate to the charger.');
      }
    } finally {
      setIsDeployingCertificate(false);
    }
  }

const onResetButtonClick = useCallback(async (callListApi: boolean) => {
  const res = await setAcFilterData(null, '', 'acChargerCertificates', 'CLEAR', false)(dispatch);
  if (res?.message === 'Action dispatched successfully') {
    if (acChargerCertificatesInfoListTablePropsRef?.current && callListApi) {
      layoutCallback(1, acChargerCertificatesInfoListTablePropsRef?.current?.pageSize, acChargerCertificatesInfoListTablePropsRef?.current?.view, acChargerCertificatesInfoListTablePropsRef?.current?.sortBy, acChargerCertificatesInfoListTablePropsRef?.current?.order, res?.filters, acChargerCertificatesInfoListTablePropsRef?.current?.searchTerm)
    }
  }
}, [acChargerCertificatesInfoListTablePropsRef?.current])

const filterFooterBtns = useMemo(() => {
  return [
    {
      buttonText: 'Reset',
      buttonId: 'Reset',
      btnClassName: 'secondary__btn',
      handleClick: (): Promise<void> => onResetButtonClick(true),
      isDisabled: false,
      buttonVariant: 'outlined',
    },
  ];
}, [onResetButtonClick]);

  const actionIconsList = useCallback(()=>{
    return [
    {
        icon: 'rms__documentation__download__icon',
        tooltipText: 'Download Client Private Key',
        fieldId:'rms-key-download-btn',
        className:'rms-support-documentation-download__icon__wrap',
        isAccessible: true,
        fileKey: 'keyFileURL',
        action: onDownloadFileIconClick
    },
    {
        icon: 'rms__documentation__download__icon',
        tooltipText: 'Download Client Certificate',
        fieldId:'rms-certificate-download-btn',
        className:'rms-support-documentation-download__icon__wrap',
        isAccessible: true,
        fileKey: 'crtFileURL',
        action: onDownloadFileIconClick
    },
    {
        icon: 'rms__documentation__download__icon',
        tooltipText: 'Download Root CA Certificate',
        fieldId:'rms-root-ca-download-btn',
        className:'rms-support-documentation-download__icon__wrap',
        isAccessible: true,
        fileKey : 'rootCaFileURL',
        action: onDownloadFileIconClick
    }
  ]
},[])
  const acChargerCertificatesInfoColumns = useMemo(() => {

    return [
      {
        field: 'visibleId',
        headerName: 'Charger ID',
        flex: .6,
        sortable: true,
        minWidth: 150,
      },
      {
        field: 'macId',
        headerName: 'MAC ID',
        flex: .6,
        sortable: true,
        minWidth: 150,
      },
      {
        field: 'ocppId',
        headerName: 'OCPP ID',
        flex: .6,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => (params?.row?.ocppId ?? '-')
      },
      {
        field: 'chargingStationName',
        headerName: 'Charging Station',
        flex: .6,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => (<CustomTooltipWithLabel label={params?.row?.chargingStationName ?? '-'} labelId={`ac-charger-charging-station-${params?.row?.id}`} />)
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: .6,
        sortable: true,
        minWidth: 130,
        renderCell: (params) => (<CustomTooltipWithLabel label={params?.row?.customerName ?? '-'} labelId={`ac-charger-customer-${params?.row?.id}`} />)
      },
      {
        field: 'chargerStatus',
        headerName: 'Charger Status',
        flex: .6,
        sortable: true,
        minWidth: 130,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const status = params?.row?.chargerStatus;
          return status != null ?
            <CustomStatus
              statusText={status}
              statusIconClassName={status === 'Online' ? 'status__online__charger__icon' : 'status__offline__charger__icon'}
              backgroundColor={status === 'Online' ? 'var(--green-color)' : 'var(--grey-color)'} />
            :
            <CustomTooltipWithLabel label={'NA'} labelId={`ac-charger-status-${params?.row?.id}`} />;
        },
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'certificateType',
        headerName: 'Certificate Type',
        flex: .6,
        sortable: true,
        minWidth: 120,
      },
      {
        field: 'certificateStatus',
        headerName: 'Certificate Status',
        flex: .6,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => {
          const status = params?.row?.certificateStatus;
        
          let backgroundColor = '';
          let color = '';
          let borderColor = '';
        
          switch (status) {
            case 'Inactive':
              color = '#5F6B7A';
              backgroundColor = '#F1F4F7';
              borderColor = '#C7D0D9';
              break;
            
            case 'Active':
              color = '#2E7D32';
              backgroundColor = '#E6F4EA';
              borderColor = '#81C784';
              break;
        
            case 'Expired':
              color = '#C62828';
              backgroundColor = '#FDEAEA';
              borderColor = '#C62828';
              break;
          }
        
          return (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <span
                style={{
                  color,
                  backgroundColor,
                  border: `2px solid ${borderColor}`,
                  fontSize: '12px',
                  fontWeight: 500,
                  borderRadius: '5px',
                  padding: '4px 12px',
                  minWidth: '80px',
                  textAlign: 'center'
                }}
              >
                {status}
              </span>
            </div>
          );
        }
      },
      {
        field: 'expiryDate',
        flex: .6,
        headerName: 'Expiry Date',
        minWidth: 180,
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel>
        ): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.expiryDate);
          const formattedDate = date.toLocaleString('en-US', {
            month: 'long',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
          return formattedDate;
        },
      },
      {
        field: 'createdBy',
        flex: .6,
        headerName: 'Generated By',
        minWidth: 150,
        renderCell: (params) => (<CustomTooltipWithLabel label={params?.row?.createdBy ?? '-'} labelId={`user-${params?.row?.id}`} />)
      },
      {
        field: 'createdAt',
        flex: .6,
        headerName: 'Generated On',
        minWidth: 180,
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel>
        ): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.createdAt);
          return formatRelativeTimeForTable(date);
        },
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: .6,
        minWidth: 340,
        sortable: false,
        pinnable: false,
        renderCell: (params) => {
          const rowData = params?.row;
          return (
            <div className='rms__support__documentation__action__icon__wrap'>
              {
                actionIconsList().map(actionBtn=>{
                  if(!actionBtn?.isAccessible)return;
                  return (
                    <div key={actionBtn.fieldId} className={actionBtn.className} onClick={()=>actionBtn.action(rowData?.[actionBtn?.fileKey])}>
                      <IconWithTooltip  iconClassName={actionBtn?.icon} toolTipText={actionBtn?.tooltipText}/>
                    </div>
                  )
                })
              }
              {rowData?.certificateStatus === 'Inactive' && (
                <SelectWithActionButton
                  selectDropDownId={`ac-charger-certificate-deploy-dropdown-${rowData?.id}`}
                  selectOptions={deployOptionsList}
                  selectValue={selectedDeployOptions?.[rowData?.id]}
                  handleSelectOptionChange={(val): void => {
                    setSelectedDeployOptions((prev) => ({ ...prev, [rowData?.id]: val }));
                  }}
                  buttonIconClassName='rms__table__dashboard__export__csv__icon'
                  buttonTooltipText={deployButtonTooltip}
                  handleButtonClick={(selectedOption): void => {
                    onDeployOptionClick(rowData, selectedOption?.value);
                  }}
                />
              )}
            </div>
          )
        }
      }
    ];
  }, [selectedDeployOptions]);

  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
    const res = await setAcFilterData(val, filterKey, 'acChargerCertificates', 'SET', isGlobal ? isGlobal : false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
      if (acChargerCertificatesInfoListTablePropsRef?.current) {
        await layoutCallback(1, acChargerCertificatesInfoListTablePropsRef?.current?.pageSize, acChargerCertificatesInfoListTablePropsRef?.current?.view, acChargerCertificatesInfoListTablePropsRef?.current?.sortBy, acChargerCertificatesInfoListTablePropsRef?.current?.order, res?.filters, acChargerCertificatesInfoListTablePropsRef?.current?.searchTerm)
      }
    }
  }, [acChargerCertificatesInfoListTablePropsRef?.current, dispatch])



  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterData, searchTerm) => {
    const tableProps: IChargerListTableProps = cloneDeep(acChargerCertificatesInfoListTablePropsRef?.current)
    const filtersValue = filterData ? filterData : getCurrentAcFiltersValues('acChargerCertificates');

    await dispatch(fetchAcChargerCertificatesInfoData({
      sortBy: sortField || acChargerCertificatesInfoListTablePropsRef?.current?.sortBy,
      order: sortOrder || acChargerCertificatesInfoListTablePropsRef?.current?.order,
      pageSize: pageSize || 15,
      pageNumber,
      filters: filtersValue,
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : acChargerCertificatesInfoListTablePropsRef?.current?.searchTerm
    }));

    if (tableProps) {
      tableProps.view = view ?? 'list'
      tableProps.sortBy = sortField || acChargerCertificatesInfoListTablePropsRef?.current?.sortBy;
      tableProps.order = sortOrder || acChargerCertificatesInfoListTablePropsRef?.current?.order;
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize || 15;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setAcChargerCertificatesInfoListTableProps(tableProps))
  }, [acChargerCertificatesInfoListTablePropsRef?.current, dispatch]);

    // Handler For Column Prefrence Chnages
    const handleColumnPrefrenceChange = useCallback(async (operation,data) => {
      await dispatch(setTableColumnPrefrences('acChargerCertificatesInfo',operation,data))
   },[])

  // Redux state Clean up
  const chargerCertificatesInfoCleanUpState = useCallback(() => {
    const action = {
      type: acCertificatesTypes.CLEAR_AC_CHARGER_CERTIFICATES_INFO_LIST_DATA
    }
    dispatch(action);
  }, [])
  useEffect(() => {
    window.addEventListener('beforeunload', chargerCertificatesInfoCleanUpState);
    return (): void => {
      chargerCertificatesInfoCleanUpState()
      window.removeEventListener('beforeunload', chargerCertificatesInfoCleanUpState);
    }
  }, [])

  const chargerFilterProps: FilterProp[] = useMemo(() => {
    const filterCertificateExpiryTimeStampValue = getAcFilterSelectedValue('certificateExpiryIntervalRange', 'acChargerCertificates', false);
    const filterCertificateCreatedAtTimeStampValue = getAcFilterSelectedValue('certificateCreatedAtInterval', 'acChargerCertificates', false);

    const chargerFilters: FilterProp[] =
      [
        {
          filterLabel: 'Charging Stations',
          filterType: 'dropdown',
          filterId: 'chargingStationId',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargingStationId', 'acChargerCertificates', false),
            selectOptions: chargingStationReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargingStationId', false)
            },
            selectDropDownId: 'charger-chargingStationId-type-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Product Type',
          filterType: 'dropdown',
          filterId: 'productType',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('productType', 'acChargerCertificates', false),
            selectOptions: chargerProductTypeReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'productType', false)
            },
            selectDropDownId: 'charger-charger-type-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Chargers',
          filterType: 'dropdown',
          filterId: 'chargerId',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargerId', 'acChargerCertificates', false),
            selectOptions: chargerVisibleIdReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargerId', false)
            },
            selectDropDownId: 'alarm-chargerId-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Certificate Status',
          filterType: 'dropdown',
          filterId: 'chargerCertificateStatus',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargerCertificateStatus', 'acChargerCertificates', false),
            selectOptions: certificateStatusReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargerCertificateStatus', false)
            },
            selectDropDownId: 'charger-charger-type-filter-dropdown',
            isMultiSelect: true
          }
        },
        {
          filterLabel: 'Generated By',
          filterId: 'certificateGeneretedBy',
          filterType: 'dropdown',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('certificateGeneretedBy', 'acChargerCertificates', false),
            selectOptions: userReferenceData ?? [],
            handleSelectOptionChange: (val) : void => {
                handleFilterDataChange(val,'certificateGeneretedBy',false);
            },
            selectDropDownId: 'charger-certificate-generated-by-filter-dropdown',
            isMultiSelect: true
          } 
        },
        {
          filterLabel: 'Generated On',
          filterType: 'dateFilter',
          filterId: 'certificateCreatedAtInterval',
          dateFilterProps: {
            datePickerId: 'alarm__time__stamp__set__at__date__picker',
            datePickerClassName: '',
            selectsRange: true,
            startDate: filterCertificateCreatedAtTimeStampValue?.[0] ? new Date(filterCertificateCreatedAtTimeStampValue?.[0]) : null,
            endDate: filterCertificateCreatedAtTimeStampValue?.[1] ? new Date(filterCertificateCreatedAtTimeStampValue?.[1]) : null,
            onChange: (val): void => {
              handleFilterDataChange(val, 'certificateCreatedAtInterval', false)
            },
          }
        },
        {
          filterLabel: 'Expiry Date Range',
          filterType: 'dateFilter',
          filterId: 'certificateExpiryIntervalRange',
          dateFilterProps: {
            datePickerId: 'alarm__time__stamp__set__at__date__picker',
            datePickerClassName: '',
            selectsRange: true,
            startDate: filterCertificateExpiryTimeStampValue?.[0] ? new Date(filterCertificateExpiryTimeStampValue?.[0]) : null,
            endDate: filterCertificateExpiryTimeStampValue?.[1] ? new Date(filterCertificateExpiryTimeStampValue?.[1]) : null,
            onChange: (val): void => {
              handleFilterDataChange(val, 'certificateExpiryIntervalRange', false)
            },
          }
        }

      ]
    return chargerFilters
  }, [  chargerProductTypeReferenceData, chargingStationReferenceData,screenFilters, userReferenceData, acChargerCertificatesInfoListTablePropsRef?.current])


  return (
    <>
      <DashboardLoader showLoader={isDeployingCertificate} loaderText='Deploying certificate...' />
      <TableDashboard
        tableId="manage__ac__charger__certificate__table"
        tableClassName="manage__ac__charger__certificate__table"
        header={'Charger Certificates'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={acChargerCertificatesInfoColumns}
        tableRows={getTableRows()}
        layouts={layouts}
        layoutView={'list'}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={acChargerCertificatesInfoCount ??0}
        showSkeleton={!acChargerCertificatesInfo}
        totalCountText={'certificates'}
        showLoader={loader}
        customNoDataComponent={NoDataComponentUsbCertificates}
        pageNumber={acChargerCertificatesInfoListTableProps?.pageNumber}
        tablePageSize={acChargerCertificatesInfoListTableProps?.pageSize}
        sortField={acChargerCertificatesInfoListTableProps?.sortBy}
        sortOrder={acChargerCertificatesInfoListTableProps?.order}
        referenceDataCallbacksFilters={['']}
        columnPrefrences={screenTableColumnPrefrences?.find((item) => item.screen === 'acChargerCertificatesInfo')}
        handleColumnPrefrenceChange={handleColumnPrefrenceChange}
      />

      {filtersVisible && <SidePopover
        id={'manage__chargers__side__filters'}
        open={filtersVisible}
        containerClassName='manage__chargers__side__filters'
        handleClose={handleFiltersClosePopOver}
        headerText={' Add Filters'}
        // eslint-disable-next-line react/no-children-prop
        children={<AcFilterLayout requiredReferenceDataCallbacks={['chargerVisibleId','productType','chargingStationId','chargerCertificateStatus','user']/* ['location', 'status', 'softwareVersion', 'severity', 'chargerVisibleId'] */}
          filters={chargerFilterProps} handleFilterDataChange={handleFilterDataChange}/>}
        btnsList={filterFooterBtns}
      />
      } 
    </>
  );
})

export default AcChargerCertificatesInfo;
